import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  IconButton,
  Typography,
  CircularProgress,
  Snackbar,
  Alert,
  Grid,
} from "@mui/material";

import Close from "@mui/icons-material/Close";
import Send from "@mui/icons-material/Send";
import LocalHospitalIcon from "@mui/icons-material/LocalHospital";

import moment from "moment";
import { formatWithOutSom } from "../Message";

import axios from "axios";
import { loadAvailableScansdb, requestLab ,loadRequestedScanInpatient } from "../BaseURL";
import Swal from "sweetalert2";

const RequestScanDialog = ({ open, onClose, patient, sendstartDate }) => {

  const currentDate = moment().format("YYYY-MM-DD");

  const [scans, setScans] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");

  const [requestDate, setRequestDate] = useState("");
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [requestedScans, setRequestedScans] = useState([]);

  // 🔥 Track which scan is being requested by unique identifier
  const [requestingScanId, setRequestingScanId] = useState(null);

  useEffect(() => {
    if (!open) return;

    // Set date only once
    if (!requestDate) {
      setRequestDate(currentDate);
    }

    // Load scans ONLY when the dialog opens
    loadScans();
    loadRequestedScan()

  }, [open]);


  const loadScans = async () => {
    setLoading(true);
    try {
      const res = await axios.get(loadAvailableScansdb, {
        params: { insurance: patient.insurance },
      });

      if (res.data.success) {
        setScans(res.data.results || []);
      }
    } catch (error) {
      console.error("Error loading scans:", error);
    } finally {
      setLoading(false);
    }
  };


  const loadRequestedScan = async () => {
    if (!patient || !patient.id) return;
   setLoading(true);
    try {
      const response = await axios.get(loadRequestedScanInpatient, {
        params: {
          patientId: patient.id,
          dat:sendstartDate,
          currentDate
        },
      });
  
      if (response.data.success) {
        setRequestedScans(response.data.results);
      }
  
    } catch (err) {
      console.error("Failed to load requested labs:", err);
    }finally {
      setLoading(false);
    }
  };


  const handleRequestScan = async (scan, scanId) => {
    if (!requestDate) {
      setSnackbarMessage("❌ Please select a request date");
      setSnackbarOpen(true);
      return;
    }

    if (moment(requestDate).isBefore(moment(sendstartDate))) {
      setSnackbarMessage(`❌ Date cannot be earlier than admission date (${sendstartDate})`);
      setSnackbarOpen(true);
      return;
    }

    if (moment(requestDate).isAfter(moment(currentDate))) {
      setSnackbarMessage(`❌ Date cannot be in the future`);
      setSnackbarOpen(true);
      return;
    }

      const alreadyRequested = requestedScans.some(
        (r) => (r.lab || r.scan) === (scan.name || scan.lab) && r.date==currentDate
      );
    
      if (alreadyRequested) {
             const result = await Swal.fire({
               title: "Duplicate Request",
               text: `${scan.name} has already been requested today. Do you want to request again?`,
               icon: "warning",
               showCancelButton: true,
               confirmButtonColor: "#3085d6",
               cancelButtonColor: "#d33",
               confirmButtonText: "Yes, Request Again",
               cancelButtonText: "No"
             });
         
             if (!result.isConfirmed) {
               return; // user cancelled
             }
           }

    // 🔥 START loading only for THIS scan using unique ID
    setRequestingScanId(scanId);

    try {
      const res = await axios.post(requestLab, {
        patientId: patient.id,
        dat: requestDate,
        patientInsurance: patient.insurance,
        labName: scan.name,
        amount: scan.amount,
        base_cost: scan.tariff,
        code: scan.code,
        currentUser: patient.currentUser,
        type: "Scan",
      });

      if (!res.data.success) {
        setSnackbarMessage(`❌ ${res.data.message}`);
      } else {
        setSnackbarMessage(`✅ ${scan.name} requested successfully`);
        loadRequestedScan()
      }

      setSnackbarOpen(true);

    } catch (error) {
     
      setSnackbarMessage(`❌ Failed to request ${scan.name}`);
      setSnackbarOpen(true);
    } finally {
      // 🟢 Always clear the loading state
      setRequestingScanId(null);
    }
  };


  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle
          sx={{
            background: "linear-gradient(135deg, #19b934ff 0%, #19b934ff 100%)",
            color: "white",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            fontWeight: 700,
          }}
        >
          <span style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <LocalHospitalIcon />
            Request Scan
          </span>

          <IconButton onClick={onClose} sx={{ color: "white" }}>
            <Close />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 3 }}>

          <Grid container spacing={2} columns={{ xs: 4, sm: 8, md: 12 }}>
            <Grid item xs={12} md={8} size={6}>
              <TextField
                fullWidth
                placeholder="Search scan..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                size="small"
                sx={{ mt: 2 }}
              />
            </Grid>

            <Grid item xs={12} md={4} size={6}>
              <TextField
                fullWidth
                label="Request Date"
                type="date"
                value={requestDate}
                onChange={(e) => setRequestDate(e.target.value)}
                size="small"
                InputLabelProps={{ shrink: true }}
                sx={{ mt: 2 }}
              />
            </Grid>
          </Grid>

          {loading && (
            <Typography sx={{ textAlign: "center", py: 3 }}>
              <CircularProgress size={28} />
            </Typography>
          )}

          {!loading && (
            <Paper sx={{ p: 1, mt: 2 }}>
              <TableContainer sx={{ maxHeight: 400 }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: "bold" }}>Scan</TableCell>
                      <TableCell sx={{ fontWeight: "bold" }}>Amount</TableCell>
                      <TableCell sx={{ fontWeight: "bold" }} align="center">
                        Request
                      </TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {scans
                      .filter((s) =>
                        s.name.toLowerCase().includes(search.toLowerCase())
                      )
                      .map((scan, idx) => {
                        // 🔥 Create unique identifier for each scan
                        const scanId = `${scan.code}-${idx}`;
                        
                        return (
                          <TableRow key={scanId}>
                            <TableCell>{scan.name}</TableCell>
                            <TableCell>{formatWithOutSom(scan.amount)}</TableCell>

                            <TableCell align="center">
                              {requestingScanId === scanId ? (
                                <CircularProgress size={24} />
                              ) : (
                                <IconButton
                                  color="primary"
                                  onClick={() => handleRequestScan(scan, scanId)}
                                >
                                  <Send />
                                </IconButton>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 2 }}>
          <Button
            variant="contained"
            color="error"
            onClick={onClose}
            sx={{ textTransform: "none", fontWeight: 600 }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          severity={snackbarMessage.includes("❌") ? "error" : "success"}
          sx={{ fontWeight: 600 }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

export default RequestScanDialog;